"use client"
import { useState, useEffect, useMemo, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as Checkbox from '@radix-ui/react-checkbox';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import {
  Table,
  Button,
  Badge,
  Text,
  Flex,
  Box
} from '@radix-ui/themes';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  Pencil,
  Trash2,
  Eye,
  CheckCircle,
  Clock,
  FileText,
  Settings,
  Search,
  ExternalLink,
  Globe,
  Loader2,
  Lock,
  RefreshCw,
  Plus,
  Check,
  BarChart3,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import ServiceMetaDeleteModal from './ServiceMetaDeleteModal';
import ServiceMetaSeoModal from './ServiceMetaSeoModal';
import {
  buildAdminPageExistenceSet,
  getAdminPageNotFoundReason,
  getRedirectsAppliedToPage,
  isPermanentNoIndexPage,
  isSuspiciousNotFoundPage,
  isUsVersionPage,
  normalizeAdminPagePath
} from '@/lib/admin/servicePageFilters';

interface ServiceMeta {
  _id: string;
  serviceName: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  status: 'Draft' | 'Published';
  createdAt: string;
  updatedAt: string;
  lastModified?: string;
  indexed?: boolean;
  isPermanentNoIndex?: boolean;
  isVisible?: boolean;
  hasMeta?: boolean;
  notFoundReason?: string;
  suspiciousReason?: string;
}

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  status: 'Draft' | 'Published';
  author?: string;
  editor?: string;
  publishDate?: string;
  updatedAt: string;
  createdAt: string;
  lastModified?: string;
  postType?: 'blog' | 'news-pr' | 'thought-leadership';
}

interface ServiceMetaFilters {
  searchTerm: string;
  selectedStatus: string;
  selectedDateRange: string;
  dateFrom?: string;
  dateTo?: string;
  /** Category tab from dashboard: All, Service Pages, Blogs, News Pr, etc. */
  categoryTab?: string;
  /** @deprecated Use categoryTab. Filter by content type when categoryTab not used */
  contentType?: 'All' | 'Service Pages' | 'Blogs' | 'News' | 'Thought Leadership';
}

interface ServiceMetaListProps {
  filters: ServiceMetaFilters;
  onRefresh?: () => void;
}

interface ServiceMetaResponse {
  items: ServiceMeta[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface RedirectItem {
  _id: string;
  sourceUrl?: string;
  destinationUrl?: string;
  type?: 301 | 302 | 410;
  isActive?: boolean;
}

const statusConfig = {
  Published: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  Draft: { color: 'bg-yellow-100 text-yellow-800', icon: Clock }
};

const toRedirectHref = (value?: string) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '#';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const normalizedPath = normalizeAdminPagePath(trimmed);
  return normalizedPath ? `/${normalizedPath}` : '/';
};

const getSeoScoreColor = (score: number) => {
  if (score >= 90) return 'bg-green-100 text-green-800';
  if (score >= 80) return 'bg-blue-100 text-blue-800';
  if (score >= 70) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

// Menu structure based on navbar
interface MenuCategory {
  name: string;
  slugPatterns?: string[];
  subCategories?: { name: string; slugPatterns: string[] }[];
}

const menuCategories: MenuCategory[] = [
  {
    name: 'Service Pages',
    subCategories: [
      {
        name: 'Design/Development-as-a-Service',
        slugPatterns: ['design-development-as-a-service', 'ui-ux-design', 'website-development', 'graphic-design']
      },
      {
        name: 'Growth-as-a-Service',
        slugPatterns: ['growth-as-a-service', 'organic-visibility', 'performance-marketing', 'content-marketing']
      },
      {
        name: 'AI-as-a-Service',
        slugPatterns: ['ai-as-a-service']
      },
      {
        name: 'Staff-as-a-Service',
        slugPatterns: ['staff-as-a-service']
      },
      {
        name: 'About Us',
        slugPatterns: ['about-us']
      },
      {
        name: 'BDLC',
        slugPatterns: ['brand-development-lifecycle', 'bdlc']
      }
    ]
  },
  {
    name: 'A to Z of Branding',
    slugPatterns: ['a-to-z-of-branding']
  },
  {
    name: 'Industries',
    slugPatterns: ['industries/healthcare', 'industries/real-estate', 'industries/e-commerce', 'industries/hospitality', 'industries/ed-tech', 'healthcare', 'real-estate', 'e-commerce', 'hospitality', 'ed-tech']
  },
  {
    name: 'Success Stories',
    subCategories: [
      {
        name: 'Case Studies',
        slugPatterns: ['case-study', 'case-studies']
      },
      {
        name: 'Testimonial',
        slugPatterns: ['testimonial', 'testimonials']
      }
    ]
  },
  {
    name: 'Resources',
    slugPatterns: ['blogs', 'news-pr', 'thought-leadership', 'resources']
  },
  {
    name: 'Other Supporting Pages',
    slugPatterns: ['privacy-policy', 'cookies', 'terms-conditions', 'contact-us', 'thankyou', 'thank-you', 'coming-soon']
  },
  {
    name: 'Blogs',
    slugPatterns: ['blogs']
  },
  {
    name: 'News Pr',
    slugPatterns: ['news-pr']
  },
  {
    name: 'ThoughtLeadership',
    slugPatterns: ['thought-leadership']
  }
];

// Function to categorize a page based on its slug
const categorizePage = (slug: string): { category: string; subCategory?: string } => {
  const normalizedSlug = slug.toLowerCase().replace(/^\/+/, '').replace(/\/+$/, '');

  // Helper function to check if slug matches a pattern
  const matchesPattern = (slug: string, pattern: string): boolean => {
    const normalizedPattern = pattern.toLowerCase();
    // Exact match
    if (slug === normalizedPattern) return true;
    // Starts with pattern followed by / (for nested routes)
    if (slug.startsWith(normalizedPattern + '/')) return true;
    return false;
  };

  // Check categories in order of specificity

  // 1. Check A to Z of Branding (exact match or starts with)
  if (normalizedSlug === 'a-to-z-of-branding' || normalizedSlug.startsWith('a-to-z-of-branding/')) {
    return { category: 'A to Z of Branding' };
  }

  // 2. Check Service Pages subcategories first (most specific)
  const servicePagesCategory = menuCategories.find(c => c.name === 'Service Pages');
  if (servicePagesCategory?.subCategories) {
    for (const subCategory of servicePagesCategory.subCategories) {
      for (const pattern of subCategory.slugPatterns) {
        if (matchesPattern(normalizedSlug, pattern)) {
          return { category: 'Service Pages', subCategory: subCategory.name };
        }
      }
    }
  }

  // 3. Check Success Stories subcategories
  const successStoriesCategory = menuCategories.find(c => c.name === 'Success Stories');
  if (successStoriesCategory?.subCategories) {
    for (const subCategory of successStoriesCategory.subCategories) {
      for (const pattern of subCategory.slugPatterns) {
        if (matchesPattern(normalizedSlug, pattern)) {
          return { category: 'Success Stories', subCategory: subCategory.name };
        }
      }
    }
  }

  // 4. Check Industries (with /industries/ prefix or exact match)
  if (normalizedSlug.startsWith('industries/')) {
    return { category: 'Industries' };
  }
  const industryPatterns = ['healthcare', 'real-estate', 'e-commerce', 'hospitality', 'ed-tech'];
  for (const pattern of industryPatterns) {
    if (normalizedSlug === pattern.toLowerCase()) {
      return { category: 'Industries' };
    }
  }

  // 5. Check Blogs (exact match or starts with) - check before other patterns
  if (normalizedSlug === 'blogs' || normalizedSlug.startsWith('blogs/')) {
    return { category: 'Blogs' };
  }

  // 6. Check News Pr (exact match or starts with)
  if (normalizedSlug === 'news-pr' || normalizedSlug.startsWith('news-pr/')) {
    return { category: 'News Pr' };
  }

  // 7. Check ThoughtLeadership (exact match or starts with)
  if (normalizedSlug === 'thought-leadership' || normalizedSlug.startsWith('thought-leadership/')) {
    return { category: 'ThoughtLeadership' };
  }

  // 8. Check Resources (exact match only - these are top-level routes)
  if (normalizedSlug === 'resources') {
    return { category: 'Resources' };
  }

  // 9. Check Other Supporting Pages
  const otherPagesPatterns = ['privacy-policy', 'cookies', 'terms-conditions', 'contact-us', 'thankyou', 'thank-you', 'coming-soon'];
  for (const pattern of otherPagesPatterns) {
    if (matchesPattern(normalizedSlug, pattern)) {
      return { category: 'Other Supporting Pages' };
    }
  }

  // 10. Default to "Other Supporting Pages" for any unmatched pages
  return { category: 'Other Supporting Pages' };
};

/** Get the display category for an item (for filtering by category tab) */
function getCategoryForItem(item: ServiceMeta & { postType?: string }): string {
  const postType = item.postType;
  if (postType === 'blog') return 'Blogs';
  if (postType === 'news-pr') return 'News Pr';
  if (postType === 'thought-leadership') return 'ThoughtLeadership';
  return categorizePage(item.slug).category;
}

export default function ServiceMetaList({ filters, onRefresh }: ServiceMetaListProps) {
  const { data: session } = useSession();
  const router = useRouter();
  const [serviceMetas, setServiceMetas] = useState<Array<ServiceMeta & { postType?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedServiceMetaForDelete, setSelectedServiceMetaForDelete] = useState<ServiceMeta | null>(null);
  const [isSeoModalOpen, setIsSeoModalOpen] = useState(false);
  const [selectedServiceMetaForSeo, setSelectedServiceMetaForSeo] = useState<ServiceMeta | null>(null);
  const [suspiciousDeleteOpen, setSuspiciousDeleteOpen] = useState(false);
  const [suspiciousDeleteLoading, setSuspiciousDeleteLoading] = useState(false);
  const [suspiciousDeletePassword, setSuspiciousDeletePassword] = useState('');
  const [suspiciousDeleteError, setSuspiciousDeleteError] = useState<string | null>(null);
  const [suspiciousDeletePage, setSuspiciousDeletePage] = useState<ServiceMeta | null>(null);
  const [redirectItems, setRedirectItems] = useState<RedirectItem[]>([]);
  const [appliedRedirectModalOpen, setAppliedRedirectModalOpen] = useState(false);
  const [appliedRedirectModalPage, setAppliedRedirectModalPage] = useState<(ServiceMeta & { postType?: string }) | null>(null);
  const [redirectingPagesModalOpen, setRedirectingPagesModalOpen] = useState(false);
  const [redirectingPagesModalPage, setRedirectingPagesModalPage] = useState<(ServiceMeta & { postType?: string }) | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedSubCategories, setExpandedSubCategories] = useState<Set<string>>(new Set());
  const adminEmail = session?.user?.email || '';

  // When a single category tab is selected, auto-expand that category so items are visible
  useEffect(() => {
    const tab = filters.categoryTab;
    if (tab && tab !== 'All') {
       
      setExpandedCategories((prev) => new Set(prev).add(tab));
    }
  }, [filters.categoryTab]);

  // Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => filters, [filters]);

  // Check if any filters are applied
  const isFiltering = useMemo(() => {
    return (
      filters.searchTerm.trim() !== '' ||
      filters.selectedStatus !== 'All' ||
      filters.selectedDateRange !== 'All Time' ||
      filters.dateFrom !== undefined ||
      filters.dateTo !== undefined
    );
  }, [filters]);

  const formatLastModified = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)} day${Math.floor(diffInDays) > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }, []);

  const fetchServiceMetas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters from filters - fetch all records without pagination
      const queryParams = new URLSearchParams({
        page: '1',
        limit: '0', // 0 means fetch all records
      });

      // Add filter parameters
      if (memoizedFilters.searchTerm) {
        queryParams.append('search', memoizedFilters.searchTerm);
      }
      if (memoizedFilters.selectedStatus !== 'All') {
        queryParams.append('status', memoizedFilters.selectedStatus);
      }
      if (memoizedFilters.selectedDateRange !== 'All Time') {
        queryParams.append('dateRange', memoizedFilters.selectedDateRange);
      }
      if (memoizedFilters.dateFrom) {
        queryParams.append('dateFrom', memoizedFilters.dateFrom);
      }
      if (memoizedFilters.dateTo) {
        queryParams.append('dateTo', memoizedFilters.dateTo);
      }

      // Build post query parameters
      const postQueryParams = new URLSearchParams({
        page: '1',
        limit: '0', // Fetch all posts
      });

      if (memoizedFilters.selectedStatus !== 'All') {
        postQueryParams.append('status', memoizedFilters.selectedStatus);
      }

      if (memoizedFilters.searchTerm) {
        postQueryParams.append('search', memoizedFilters.searchTerm);
      }

      const shouldLoadStaticUsPages = memoizedFilters.categoryTab === 'US Version Pages';

      // Fetch service metas and posts in parallel
      const [serviceMetaRes, blogsRes, newsPrRes, thoughtLeadershipRes, slugsRes, redirectsRes] = await Promise.all([
        fetch(`/api/admin/service-meta?${queryParams.toString()}`),
        fetch(`/api/admin/blogs?${postQueryParams.toString()}`),
        fetch(`/api/admin/news-pr?${postQueryParams.toString()}`),
        fetch(`/api/admin/thought-leadership?${postQueryParams.toString()}`),
        fetch('/api/all-slugs'),
        fetch('/api/admin/redirects?statusCodeGroup=redirect&status=active&limit=10000').catch(() => null)
      ]);

      if (!serviceMetaRes.ok) {
        throw new Error('Failed to fetch service metas');
      }

      const serviceMetaData: ServiceMetaResponse = await serviceMetaRes.json();
      let allSlugsRaw: string[] = [];
      const existingPageSlugs = new Set<string>();
      if (redirectsRes?.ok) {
        const redirectsPayload = await redirectsRes.json().catch(() => ({ redirects: [] as RedirectItem[] }));
        setRedirectItems(Array.isArray(redirectsPayload.redirects) ? redirectsPayload.redirects : []);
      } else {
        setRedirectItems([]);
      }

      if (!slugsRes.ok) {
        throw new Error('Failed to fetch site slugs');
      }

      const slugsPayload = await slugsRes.json().catch(() => ({ slugs: [] as string[] }));
      allSlugsRaw = Array.isArray(slugsPayload.slugs) ? slugsPayload.slugs : [];

      buildAdminPageExistenceSet(allSlugsRaw).forEach((slugPath) => {
        existingPageSlugs.add(slugPath);
      });

      // Fetch posts data (blogs, news-pr, thought-leadership)
      let blogsData: BlogPost[] = [];
      let newsPrData: BlogPost[] = [];
      let thoughtLeadershipData: BlogPost[] = [];

      if (blogsRes.ok) {
        const blogsResponse = await blogsRes.json();
        blogsData = (blogsResponse.blogs || []).map((post: BlogPost) => ({
          ...post,
          lastModified: formatLastModified(post.updatedAt),
          postType: 'blog' as const
        }));
      }

      if (newsPrRes.ok) {
        const newsPrResponse = await newsPrRes.json();
        newsPrData = (newsPrResponse.newsPr || []).map((post: BlogPost) => ({
          ...post,
          lastModified: formatLastModified(post.updatedAt),
          postType: 'news-pr' as const
        }));
      }

      if (thoughtLeadershipRes.ok) {
        const thoughtLeadershipResponse = await thoughtLeadershipRes.json();
        thoughtLeadershipData = (thoughtLeadershipResponse.thoughtLeadership || []).map((post: BlogPost) => ({
          ...post,
          lastModified: formatLastModified(post.updatedAt),
          postType: 'thought-leadership' as const
        }));
      }

      // Combine service metas with posts
      // Convert posts to ServiceMeta-like format for display
      const postsAsServiceMeta: Array<ServiceMeta & { postType?: string }> = [
        ...blogsData.map(post => ({
          _id: post._id,
          serviceName: post.title,
          slug: `blogs/${post.slug}`,
          metaTitle: post.title,
          metaDescription: '',
          canonicalUrl: `https://makemybrandlabs.com/blogs/${post.slug}`,
          status: post.status,
          createdAt: post.createdAt,
          updatedAt: post.updatedAt,
          lastModified: post.lastModified || formatLastModified(post.updatedAt),
          postType: 'blog' as const
        })),
        ...newsPrData.map(post => ({
          _id: post._id,
          serviceName: post.title,
          slug: `news-pr/${post.slug}`,
          metaTitle: post.title,
          metaDescription: '',
          canonicalUrl: `https://makemybrandlabs.com/news-pr/${post.slug}`,
          status: post.status,
          createdAt: post.createdAt,
          updatedAt: post.updatedAt,
          lastModified: post.lastModified || formatLastModified(post.updatedAt),
          postType: 'news-pr' as const
        })),
        ...thoughtLeadershipData.map(post => ({
          _id: post._id,
          serviceName: post.title,
          slug: `thought-leadership/${post.slug}`,
          metaTitle: post.title,
          metaDescription: '',
          canonicalUrl: `https://makemybrandlabs.com/thought-leadership/${post.slug}`,
          status: post.status,
          createdAt: post.createdAt,
          updatedAt: post.updatedAt,
          lastModified: post.lastModified || formatLastModified(post.updatedAt),
          postType: 'thought-leadership' as const
        }))
      ];

      // Add lastModified field for service metas
      const serviceMetasWithLastModified = serviceMetaData.items.map(service => {
        const normalizedSlug = normalizeAdminPagePath(service.slug || service.canonicalUrl);
        const notFoundReason = getAdminPageNotFoundReason(service, existingPageSlugs);

        const withLastModified: ServiceMeta & { postType?: string } = {
          ...service,
          slug: normalizedSlug || service.slug,
          lastModified: formatLastModified(service.updatedAt),
          hasMeta: true,
        };

        if (notFoundReason) {
          withLastModified.notFoundReason = notFoundReason;
        }

        return withLastModified;
      });

      const existingMetaSlugs = new Set(
        serviceMetasWithLastModified.map((service) => normalizeAdminPagePath(service.slug))
      );
      let staticUsRows: Array<ServiceMeta & { postType?: string }> = [];

      if (shouldLoadStaticUsPages) {
        const nowIso = new Date().toISOString();

        staticUsRows = allSlugsRaw
          .map((slugPath) => normalizeAdminPagePath(slugPath))
          .filter((slugPath, index, allSlugs) => {
            const normalized = slugPath.toLowerCase();
            return (
              Boolean(slugPath) &&
              allSlugs.findIndex((candidate) => candidate.toLowerCase() === normalized) === index &&
              !existingMetaSlugs.has(normalized) &&
              isUsVersionPage({ slug: slugPath })
            );
          })
          .map((slugPath) => {
            const lastSegment = slugPath.split('/').filter(Boolean).pop() || slugPath;
            const serviceName = lastSegment
              .replace(/[-_]/g, ' ')
              .replace(/\b\w/g, (char) => char.toUpperCase());

            return {
              _id: `static:${slugPath}`,
              serviceName,
              slug: slugPath,
              metaTitle: serviceName,
              metaDescription: '',
              canonicalUrl: `https://makemybrandlabs.com/${slugPath}`,
              status: 'Published' as const,
              createdAt: nowIso,
              updatedAt: nowIso,
              lastModified: formatLastModified(nowIso),
              indexed: true,
              hasMeta: false
            };
          });
      }

      // Combine all items
      const allItems: Array<ServiceMeta & { postType?: string }> = [
        ...serviceMetasWithLastModified,
        ...postsAsServiceMeta,
        ...staticUsRows
      ];

      setServiceMetas(allItems);
      setHasFiltersApplied(isFiltering);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch service metas');
    } finally {
      setLoading(false);
    }
  }, [memoizedFilters, isFiltering, formatLastModified]);

  // Fetch service metas from API when filters change
  useEffect(() => {
    fetchServiceMetas();
  }, [memoizedFilters, fetchServiceMetas]);



  const handleSelectService = (serviceId: string) => {
    if (selectedServices.includes(serviceId)) {
      setSelectedServices(selectedServices.filter(id => id !== serviceId));
    } else {
      setSelectedServices([...selectedServices, serviceId]);
    }
  };

  const handleDeleteServiceMeta = async (serviceId: string) => {
    try {
      const response = await fetch(`/api/admin/service-meta/${serviceId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete service meta');
      }

      // Refresh the service meta list
      await fetchServiceMetas();
      setIsDeleteModalOpen(false);
      setSelectedServiceMetaForDelete(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete service meta');
    }
  };

  const openSuspiciousDeleteDialog = (serviceMeta: ServiceMeta) => {
    if (!serviceMeta || !serviceMeta._id || !serviceMeta.serviceName) {
      setError('Cannot delete suspicious page data: Invalid data');
      return;
    }

    setSuspiciousDeletePage(serviceMeta);
    setSuspiciousDeletePassword('');
    setSuspiciousDeleteError(null);
    setSuspiciousDeleteOpen(true);
    setError(null);
  };

  const performDeleteSuspiciousServiceMeta = async () => {
    const target = suspiciousDeletePage;
    if (!target) return;
    if (!suspiciousDeletePassword.trim()) {
      setSuspiciousDeleteError('Admin password is required');
      return;
    }

    try {
      setSuspiciousDeleteLoading(true);
      setSuspiciousDeleteError(null);

      const response = await fetch(`/api/admin/service-meta/${target._id}/verified-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: suspiciousDeletePassword }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete suspicious page data');
      }

      setSuspiciousDeleteOpen(false);
      setSuspiciousDeletePage(null);
      setSuspiciousDeletePassword('');
      await fetchServiceMetas();
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setSuspiciousDeleteError(err instanceof Error ? err.message : 'Failed to delete suspicious page data');
    } finally {
      setSuspiciousDeleteLoading(false);
    }
  };

  const openDeleteModal = (serviceMeta: ServiceMeta) => {
    if (!serviceMeta || !serviceMeta._id || !serviceMeta.serviceName) {
      console.error('Invalid service meta provided to openDeleteModal:', serviceMeta);
      setError('Cannot delete service meta: Invalid data');
      return;
    }

    setSelectedServiceMetaForDelete(serviceMeta);
    setIsDeleteModalOpen(true);
    // Clear any existing errors
    setError(null);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedServiceMetaForDelete(null);
  };

  const openSeoModal = (serviceMeta: ServiceMeta) => {
    if (!serviceMeta || !serviceMeta._id) {
      console.error('Invalid service meta provided to openSeoModal:', serviceMeta);
      return;
    }

    setSelectedServiceMetaForSeo(serviceMeta);
    setIsSeoModalOpen(true);
  };

  const ensureServiceMetaForPage = async (
    page: ServiceMeta & { postType?: string }
  ): Promise<ServiceMeta & { postType?: string }> => {
    if (page.hasMeta !== false && page._id && !page._id.startsWith('static:')) {
      return page;
    }

    const normalizedSlug = String(page.slug || '').replace(/^\/+/, '').replace(/\/+$/, '');
    const metaDescription =
      page.metaDescription?.trim() || `Learn more about ${page.serviceName} on Make My Brand.`;
    const payload = {
      serviceName: page.serviceName,
      slug: normalizedSlug,
      metaTitle: page.metaTitle || page.serviceName,
      metaDescription,
      canonicalUrl: page.canonicalUrl || `https://makemybrandlabs.com/${normalizedSlug}`,
      status: page.status || 'Published',
      indexed: page.indexed ?? true,
      isVisible: page.isVisible ?? true,
    };

    const response = await fetch('/api/admin/service-meta', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.id) {
      throw new Error(data.error || 'Failed to create service meta for page');
    }

    const ensured: ServiceMeta & { postType?: string } = {
      ...page,
      _id: data.id as string,
      slug: normalizedSlug,
      metaTitle: payload.metaTitle,
      metaDescription,
      canonicalUrl: payload.canonicalUrl,
      indexed: payload.indexed,
      isVisible: payload.isVisible,
      hasMeta: true,
    };

    setServiceMetas((current) =>
      current.map((item) => (item._id === page._id ? ensured : item))
    );

    return ensured;
  };

  const openSeoModalForService = async (service: ServiceMeta & { postType?: string }) => {
    try {
      setError(null);
      const ensured = await ensureServiceMetaForPage(service);
      openSeoModal(ensured);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open SEO analysis');
    }
  };

  const goToServiceMetaRoute = async (
    service: ServiceMeta & { postType?: string },
    route: 'edit' | 'view'
  ) => {
    try {
      setError(null);
      const ensured = await ensureServiceMetaForPage(service);
      router.push(`/admin/service-meta/${ensured._id}/${route}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open service meta page');
    }
  };

  const closeSeoModal = () => {
    setIsSeoModalOpen(false);
    setSelectedServiceMetaForSeo(null);
  };

  const getStatusConfig = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig] || statusConfig.Draft;
  };

  const calculateSeoScore = (service: ServiceMeta) => {
    let score = 50; // Base score

    // Meta title length (optimal: 30-60 characters)
    const titleLength = service.metaTitle?.length || 0;
    if (titleLength >= 30 && titleLength <= 60) score += 20;
    else if (titleLength >= 20 && titleLength <= 70) score += 10;

    // Meta description length (optimal: 120-160 characters)
    const descLength = service.metaDescription?.length || 0;
    if (descLength >= 120 && descLength <= 160) score += 20;
    else if (descLength >= 100 && descLength <= 180) score += 10;

    // OG tags present
    if (service.ogTitle && service.ogDescription && service.ogImage) score += 10;

    return Math.min(100, score);
  };

  // Filter by category tab (from dashboard) or legacy contentType
  const displayedServiceMetas = useMemo(() => {
    const categoryTab = filters.categoryTab;
    if (categoryTab && categoryTab !== 'All') {
      if (categoryTab === 'US Version Pages') {
        return serviceMetas.filter((item) => isUsVersionPage(item));
      }
      if (categoryTab === 'Fixed NoIndex Pages') {
        return serviceMetas.filter((item) => isPermanentNoIndexPage(item));
      }
      if (categoryTab === 'Suspicious Not Found') {
        return serviceMetas.filter((item) => isSuspiciousNotFoundPage(item));
      }
      return serviceMetas.filter((item) =>
        !isUsVersionPage(item) &&
        !isSuspiciousNotFoundPage(item) &&
        getCategoryForItem(item as ServiceMeta & { postType?: string }) === categoryTab
      );
    }
    const type = filters.contentType;
    if (!type || type === 'All') return serviceMetas;
    return serviceMetas.filter((item) => {
      const postType = (item as ServiceMeta & { postType?: string }).postType;
      if (type === 'Service Pages') return !postType;
      if (type === 'Blogs') return postType === 'blog';
      if (type === 'News') return postType === 'news-pr';
      if (type === 'Thought Leadership') return postType === 'thought-leadership';
      return true;
    });
  }, [serviceMetas, filters.categoryTab, filters.contentType]);
  const isTableCategoryView = Boolean(
    filters.categoryTab &&
    filters.categoryTab !== 'All' &&
    filters.categoryTab !== 'Service Pages'
  );

  const getRedirectingPagesForService = useCallback((page: ServiceMeta & { postType?: string }) => {
    const targetPaths = new Set(
      [page.slug, page.canonicalUrl]
        .map((value) => normalizeAdminPagePath(value))
        .filter(Boolean)
    );

    return redirectItems.filter((redirect) => {
      if (redirect.isActive === false) return false;
      const destinationPath = normalizeAdminPagePath(redirect.destinationUrl);
      return destinationPath ? targetPaths.has(destinationPath) : false;
    });
  }, [redirectItems]);

  const appliedRedirectModalLinks = useMemo(() => {
    return appliedRedirectModalPage ? getRedirectsAppliedToPage(appliedRedirectModalPage, redirectItems) : [];
  }, [appliedRedirectModalPage, redirectItems]);

  const redirectingPagesModalLinks = useMemo(() => {
    return redirectingPagesModalPage ? getRedirectingPagesForService(redirectingPagesModalPage) : [];
  }, [getRedirectingPagesForService, redirectingPagesModalPage]);

  // Group pages by category
  const groupedPages = useMemo(() => {
    const groups: Record<string, Record<string, (ServiceMeta & { postType?: string })[]>> = {};

    displayedServiceMetas.forEach(service => {
      // Check if this is a post (blog, news-pr, or thought-leadership)
      const postType = (service as ServiceMeta & { postType?: string }).postType;

      let category: string;
      let subCategory: string | undefined;

      if (postType === 'blog') {
        category = 'Blogs';
      } else if (postType === 'news-pr') {
        category = 'News Pr';
      } else if (postType === 'thought-leadership') {
        category = 'ThoughtLeadership';
      } else {
        // Regular service page - categorize normally
        const categorized = categorizePage(service.slug);
        category = categorized.category;
        subCategory = categorized.subCategory;
      }

      if (!groups[category]) {
        groups[category] = {};
      }

      const groupKey = subCategory || 'main';
      if (!groups[category][groupKey]) {
        groups[category][groupKey] = [];
      }

      groups[category][groupKey].push(service as ServiceMeta & { postType?: string });
    });

    return groups;
  }, [displayedServiceMetas]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const toggleSubCategory = (category: string, subCategory: string) => {
    const key = `${category}-${subCategory}`;
    setExpandedSubCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Render service row component
  const renderServiceRow = (service: ServiceMeta & { postType?: string }) => {
    const status = getStatusConfig(service.status);
    const StatusIcon = status.icon;
    const seoScore = calculateSeoScore(service);
    const isPost = service.postType !== undefined;
    const hasMeta = service.hasMeta !== false;
    const isSuspiciousView = filters.categoryTab === 'Suspicious Not Found';
    const showPermanentNoIndexBadge = filters.categoryTab === 'Fixed NoIndex Pages' && isPermanentNoIndexPage(service);
    const appliedRedirects = getRedirectsAppliedToPage(service, redirectItems);
    const renderServiceActions = () => {
      if (!isPost) {
        return (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void openSeoModalForService(service);
              }}
              title="View SEO Analysis"
              className="p-1.5 rounded hover:bg-green-50 text-green-600 transition-colors"
            >
              <BarChart3 className="h-4 w-4" />
            </button>
            {hasMeta ? (
              <>
                <Link
                  href={`/admin/service-meta/${service._id}/edit`}
                  className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Pencil className="h-4 w-4" />
                </Link>
                <Link
                  href={`/admin/service-meta/${service._id}/view`}
                  className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Eye className="h-4 w-4" />
                </Link>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void goToServiceMetaRoute(service, 'edit');
                  }}
                  className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                  title="Edit service meta"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void goToServiceMetaRoute(service, 'view');
                  }}
                  className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600 transition-colors"
                  title="View service meta"
                >
                  <Eye className="h-4 w-4" />
                </button>
              </>
            )}
          </>
        );
      }

      return (
        <>
          {service.postType === 'blog' && (
            <Link
              href={`/admin/blogs/${service._id}/edit`}
              className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {service.postType === 'news-pr' && (
            <Link
              href={`/admin/news-pr/${service._id}/edit`}
              className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {service.postType === 'thought-leadership' && (
            <Link
              href={`/admin/thought-leadership/${service._id}/edit`}
              className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Pencil className="h-4 w-4" />
            </Link>
          )}
        </>
      );
    };

    return (
      <div
        key={service._id}
        className="px-10 py-3 border-b border-gray-100 hover:bg-blue-50/50 transition-colors"
      >
        <Flex align="center" gap="4" justify="between">
          <Flex align="center" gap="3" className="flex-1 min-w-0">
            <Checkbox.Root
              checked={selectedServices.includes(service._id)}
              onCheckedChange={() => handleSelectService(service._id)}
              className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-primary data-[state=checked]:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <Checkbox.Indicator>
                <Check className="h-3 w-3 text-white" />
              </Checkbox.Indicator>
            </Checkbox.Root>
            <Box className="flex-shrink-0 p-1.5 bg-blue-100 rounded">
              <Settings className="h-4 w-4 text-blue-600" />
            </Box>
            <Box className="flex-1 min-w-0">
              <Text weight="medium" size="2" className="text-gray-900 truncate block">
                {service.serviceName}
              </Text>
              <Text size="1" className="text-gray-500 truncate font-mono">
                /{service.slug}
              </Text>
              {!hasMeta && (
                <div className="mt-1">
                  <Badge variant="soft" color="orange" size="1">
                    No SEO meta configured
                  </Badge>
                </div>
              )}
              {service.notFoundReason && (
                <div className="mt-1">
                  <Badge variant="soft" color="red" size="1">
                    {service.notFoundReason}
                  </Badge>
                </div>
              )}
            </Box>
          </Flex>

          <Flex align="center" gap="3">
            <Badge variant="soft" color={
              service.status === 'Published' ? 'green' :
                service.status === 'Draft' ? 'yellow' : 'blue'
            } size="1">
              <StatusIcon className="h-3 w-3 mr-1" />
              {service.status}
            </Badge>
            {showPermanentNoIndexBadge && (
              <Badge variant="soft" color="red" size="1">
                <Lock className="h-3 w-3 mr-1" />
                Permanent NoIndex
              </Badge>
            )}
            {!isPost && (
              <Badge variant="soft" className={getSeoScoreColor(seoScore)} size="1">
                {seoScore}/100
              </Badge>
            )}
            <div className="min-w-[150px] max-w-[190px]">
              <Text size="1" className="block text-gray-500">
                Redirection Applied
              </Text>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setAppliedRedirectModalPage(service);
                  setAppliedRedirectModalOpen(true);
                }}
                className="inline-flex items-center gap-1 rounded border border-amber-200 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700 shadow-sm hover:bg-amber-50 transition-colors"
                title="View applied redirection"
              >
                <span>{appliedRedirects.length > 0 ? 'Yes' : 'No'}</span>
              </button>
            </div>
            <Text size="1" className="text-gray-500 min-w-[80px]">
              {service.lastModified}
            </Text>
            <Flex gap="1">
              {renderServiceActions()}
              <a
                href={service.canonicalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded hover:bg-gray-100 text-gray-600 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              {!isPost && hasMeta && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSuspiciousView) {
                      openSuspiciousDeleteDialog(service);
                    } else {
                      openDeleteModal(service);
                    }
                  }}
                  title={isSuspiciousView ? 'Delete suspicious page data' : 'Delete Service Meta'}
                  className="p-1.5 rounded hover:bg-red-50 text-red-600 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </div>
    );
  };

  const renderReadOnlySwitch = (checked: boolean) => (
    <span
      className={`relative inline-flex h-5 w-10 items-center rounded transition-colors ${
        checked ? 'bg-green-200' : 'bg-red-200'
      }`}
      aria-hidden="true"
    >
      <span
        className={`inline-block h-4 w-4 transform rounded bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </span>
  );

  const renderServiceTableRow = (service: ServiceMeta & { postType?: string }) => {
    const status = getStatusConfig(service.status);
    const StatusIcon = status.icon;
    const isPost = service.postType !== undefined;
    const hasMeta = service.hasMeta !== false;
    const isSuspiciousView = filters.categoryTab === 'Suspicious Not Found';
    const showPermanentNoIndexBadge = isPermanentNoIndexPage(service);
    const appliedRedirects = getRedirectsAppliedToPage(service, redirectItems);
    const redirectingPages = getRedirectingPagesForService(service);
    const isIndexed = showPermanentNoIndexBadge ? false : service.indexed ?? true;
    const isVisible = service.isVisible ?? true;
    const liveHref = service.canonicalUrl || `/${service.slug}`;

    const postEditHref =
      service.postType === 'blog'
        ? `/admin/blogs/${service._id}/edit`
        : service.postType === 'news-pr'
          ? `/admin/news-pr/${service._id}/edit`
          : service.postType === 'thought-leadership'
            ? `/admin/thought-leadership/${service._id}/edit`
            : '';

    return (
      <Table.Row key={service._id} className={`hover:bg-gray-50 ${hasMeta ? '' : 'bg-slate-50'}`}>
        <Table.Cell>
          <Flex gap="3" align="center">
            <Box className="flex-shrink-0 p-2 bg-blue-100 rounded shadow-sm">
              <Globe className="h-5 w-5 text-blue-600" />
            </Box>
            <Box className="flex-1 min-w-0">
              <Text weight="medium" className="text-gray-900 truncate block">
                {service.serviceName}
              </Text>
              <Text size="2" className="text-gray-500 truncate">
                /{service.slug}
              </Text>
              {!hasMeta && !isPost && (
                <div className="mt-1">
                  <Badge variant="soft" color="orange" className="text-[10px]">
                    No SEO meta configured
                  </Badge>
                </div>
              )}
              {service.notFoundReason && (
                <div className="mt-1">
                  <Badge variant="soft" color="red" className="text-[10px]">
                    {service.notFoundReason}
                  </Badge>
                </div>
              )}
            </Box>
          </Flex>
        </Table.Cell>

        <Table.Cell>
          <Badge variant="soft" color={service.status === 'Published' ? 'green' : 'yellow'}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {service.status}
          </Badge>
        </Table.Cell>

        <Table.Cell>
          {showPermanentNoIndexBadge ? (
            <Badge variant="soft" color="red">
              <Lock className="h-3 w-3 mr-1" />
              Permanent NoIndex
            </Badge>
          ) : (
            <Flex align="center" gap="2">
              {renderReadOnlySwitch(isIndexed)}
              <Badge variant="soft" color={isIndexed ? 'green' : 'red'}>
                {isIndexed ? 'Indexed' : 'No Index'}
              </Badge>
            </Flex>
          )}
        </Table.Cell>

        <Table.Cell>
          <Flex align="center" gap="2">
            {renderReadOnlySwitch(isVisible)}
            <Badge variant="soft" color={isVisible ? 'green' : 'red'}>
              {isVisible ? 'Visible' : 'Hidden'}
            </Badge>
          </Flex>
        </Table.Cell>

        <Table.Cell>
          <button
            type="button"
            onClick={() => {
              setAppliedRedirectModalPage(service);
              setAppliedRedirectModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 rounded border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-700 shadow-sm hover:bg-amber-50 hover:shadow transition-colors"
            title="View applied redirection"
          >
            <span>{appliedRedirects.length > 0 ? 'Yes' : 'No'}</span>
          </button>
        </Table.Cell>

        <Table.Cell>
          <button
            type="button"
            onClick={() => {
              setRedirectingPagesModalPage(service);
              setRedirectingPagesModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 rounded border border-indigo-200 bg-white px-2.5 py-1 text-[11px] font-medium text-indigo-700 shadow-sm hover:bg-indigo-50 hover:shadow transition-colors"
            title="View redirected pages"
          >
            <span>Pages:</span>
            <span>{redirectingPages.length}</span>
          </button>
        </Table.Cell>

        <Table.Cell>
          <Flex gap="1" align="center">
            <Calendar className="h-4 w-4 text-gray-400" />
            <Text size="2" className="text-gray-500">
              {service.lastModified}
            </Text>
          </Flex>
        </Table.Cell>

        <Table.Cell>
          <Flex gap="2" align="center">
            <a
              href={liveHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded border border-blue-200 bg-white px-2 py-1 text-[11px] font-medium text-blue-700 shadow-sm hover:bg-blue-50 hover:shadow transition-colors"
              title="View live page"
            >
              <Eye className="h-3.5 w-3.5" />
              <span>View Live</span>
            </a>

            {isPost && postEditHref ? (
              <Link
                href={postEditHref}
                className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:shadow transition-colors"
                title="Edit post"
              >
                <Pencil className="h-3.5 w-3.5" />
                <span>Edit</span>
              </Link>
            ) : hasMeta ? (
              <Link
                href={`/admin/service-meta/${service._id}/edit`}
                className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:shadow transition-colors"
                title="Open SEO settings"
              >
                <Settings className="h-3.5 w-3.5" />
                <span>SEO Settings</span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => void goToServiceMetaRoute(service, 'edit')}
                className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:shadow transition-colors"
                title="Open SEO settings"
              >
                <Settings className="h-3.5 w-3.5" />
                <span>SEO Settings</span>
              </button>
            )}

            {!isPost && (
              <button
                type="button"
                onClick={() => void openSeoModalForService(service)}
                title="View SEO Analysis"
                className="p-1.5 rounded hover:bg-green-50 text-green-600 transition-colors"
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            )}

            {!isPost && hasMeta && (
              <button
                type="button"
                onClick={() => void goToServiceMetaRoute(service, 'view')}
                title="View service meta"
                className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600 transition-colors"
              >
                <Eye className="h-4 w-4" />
              </button>
            )}

            {!isPost && hasMeta && (
              <button
                type="button"
                onClick={() => {
                  if (isSuspiciousView) {
                    openSuspiciousDeleteDialog(service);
                  } else {
                    openDeleteModal(service);
                  }
                }}
                title={isSuspiciousView ? 'Delete suspicious page data' : 'Delete Service Meta'}
                className="p-1.5 rounded hover:bg-red-50 text-red-600 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </Flex>
        </Table.Cell>
      </Table.Row>
    );
  };

  if (loading) {
    return (
      <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
            <Text size="3" weight="medium" className="text-gray-900 mb-2">
              {isFiltering ? 'Filtering Service Metas...' : 'Loading Service Metas...'}
            </Text>
            <Text size="2" className="text-gray-600">
              {isFiltering
                ? 'Applying your filters to find matching content'
                : 'Fetching your service meta data from the database'
              }
            </Text>
          </div>
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <FileText className="h-6 w-6 text-red-600" />
          </div>
          <Text size="3" weight="medium" className="text-gray-900 mb-2">
            Error Loading Service Metas
          </Text>
          <Text size="2" className="text-red-600 mb-4">{error}</Text>
          <Button onClick={fetchServiceMetas} variant="soft" color="red">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </Box>
    );
  }

  if (displayedServiceMetas.length === 0 && !loading) {
    return (
      <Table.Root>
        <Table.Body>
          <Table.Row>
            <Table.Cell colSpan={6} className="text-center py-12">
              {hasFiltersApplied ? (
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                    <Search className="h-6 w-6 text-blue-600" />
                  </div>
                  <Text size="3" weight="medium" className="text-gray-900 mb-2">
                    No Service Pages Match Your Filters
                  </Text>
                  <Text size="2" className="text-gray-600 mb-6">
                    Try adjusting your search terms or filters to find what you&apos;re looking for.
                  </Text>
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center gap-3">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <Settings className="h-6 w-6 text-gray-400" />
                  </div>
                  <Text size="3" weight="medium" className="text-gray-900 mb-2">
                    No Service Pages Created Yet
                  </Text>
                  <Text size="2" className="text-gray-600 mb-6">
                    Get started by creating your first service page to manage metadata and SEO settings.
                  </Text>
                  <Link
                    href="/admin/service-meta/new"
                    className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-white px-2 py-2 text-sm font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow cursor-pointer"
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                      <Plus className="h-3.5 w-3.5" />
                    </div>
                    <span>Create Service Page</span>
                  </Link>
                </div>
              )}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table.Root>
    );
  }

  return (
    <Box className="bg-white rounded-lg shadow-sm border border-gray-200">

      {isTableCategoryView ? (
        <div className="overflow-x-auto">
          <Table.Root>
            <Table.Header>
              <Table.Row className="bg-gradient-to-r from-blue-50 to-gray-50 rounded-t-lg">
                <Table.ColumnHeaderCell className="bg-transparent rounded-tl-lg">
                  <Text weight="bold" size="2" className="text-gray-700">Service Page</Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="bg-transparent">
                  <Text weight="bold" size="2" className="text-gray-700">Status</Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="bg-transparent">
                  <Text weight="bold" size="2" className="text-gray-700">Index</Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="bg-transparent">
                  <Text weight="bold" size="2" className="text-gray-700">Visible on Live</Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="bg-transparent">
                  <Text weight="bold" size="2" className="text-gray-700">Redirection Applied</Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="bg-transparent">
                  <Text weight="bold" size="2" className="text-gray-700">Redirecting Pages</Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="bg-transparent">
                  <Text weight="bold" size="2" className="text-gray-700">Last Updated</Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="bg-transparent rounded-tr-lg">
                  <Text weight="bold" size="2" className="text-gray-700">Actions</Text>
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {displayedServiceMetas.map((service) => renderServiceTableRow(service))}
            </Table.Body>
          </Table.Root>
        </div>
      ) : (
      <div className="divide-y divide-gray-200">
        {Object.entries(groupedPages).map(([category, subGroups]) => {
          const isExpanded = expandedCategories.has(category);
          const categoryTotal = Object.values(subGroups).flat().length;

          return (
            <div key={category} className="border-b border-gray-200 last:border-b-0">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <Flex align="center" gap="3">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  )}
                  <Text weight="bold" size="3" className="text-gray-900">
                    {category}
                  </Text>
                  <Badge variant="soft" color="blue" size="1">
                    {categoryTotal} {categoryTotal === 1 ? 'page' : 'pages'}
                  </Badge>
                </Flex>
              </button>

              {/* Subcategories or Pages */}
              {isExpanded && (
                <div className="bg-gray-50">
                  {Object.entries(subGroups).map(([subGroupKey, pages]) => {
                    const categoryInfo = menuCategories.find(c => c.name === category);
                    const subCategory = categoryInfo?.subCategories?.find(sc => sc.name === subGroupKey);
                    const isSubCategoryExpanded = expandedSubCategories.has(`${category}-${subGroupKey}`);
                    const showSubCategory = subGroupKey !== 'main' && subCategory;

                    return (
                      <div key={subGroupKey} className="border-t border-gray-200 first:border-t-0">
                        {showSubCategory ? (
                          <>
                            {/* Subcategory Header */}
                            <button
                              onClick={() => toggleSubCategory(category, subGroupKey)}
                              className="w-full px-10 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                            >
                              <Flex align="center" gap="3">
                                {isSubCategoryExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-500" />
                                )}
                                <Text weight="medium" size="2" className="text-gray-700">
                                  {subGroupKey}
                                </Text>
                                <Badge variant="soft" color="gray" size="1">
                                  {pages.length}
                                </Badge>
                              </Flex>
                            </button>
                            {/* Pages under subcategory */}
                            {isSubCategoryExpanded && (
                              <div className="bg-white">
                                {pages.map((service) => renderServiceRow(service))}
                              </div>
                            )}
                          </>
                        ) : (
                          // Pages without subcategory
                          <div className="bg-white">
                            {pages.map((service) => renderServiceRow(service))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}


      {/* Service Meta Delete Modal */}
      <ServiceMetaDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        serviceMeta={selectedServiceMetaForDelete}
        onConfirmDelete={handleDeleteServiceMeta}
      />

      <AlertDialog.Root
        open={redirectingPagesModalOpen}
        onOpenChange={(open) => {
          setRedirectingPagesModalOpen(open);
          if (!open) {
            setRedirectingPagesModalPage(null);
          }
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[205] animate-in fade-in" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-xl shadow-2xl z-[206] border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded bg-indigo-100 flex items-center justify-center">
                  <ArrowRight className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <AlertDialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                    Redirecting Pages
                    {redirectingPagesModalPage ? `: ${redirectingPagesModalPage.serviceName}` : ''}
                  </AlertDialog.Title>
                  <AlertDialog.Description className="text-sm text-gray-600 leading-relaxed">
                    {redirectingPagesModalLinks.length > 0
                      ? `These source links redirect to /${redirectingPagesModalPage?.slug || ''}.`
                      : `No active redirecting pages were found for /${redirectingPagesModalPage?.slug || ''}.`}
                  </AlertDialog.Description>
                </div>
              </div>

              <div className="mt-5 max-h-[360px] overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {redirectingPagesModalLinks.length > 0 ? (
                  redirectingPagesModalLinks.map((redirect) => (
                    <div key={redirect._id} className="p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {redirect.sourceUrl || 'Untitled redirect'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          Redirects to {redirect.destinationUrl || `/${redirectingPagesModalPage?.slug || ''}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {redirect.type && (
                          <Badge variant="soft" color="blue" size="1">
                            {redirect.type}
                          </Badge>
                        )}
                        {redirect.sourceUrl && (
                          <a
                            href={toRedirectHref(redirect.sourceUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 transition-colors"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span>Open</span>
                          </a>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-sm text-gray-500">
                    No redirected links are pointing to this page.
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-center justify-end">
                <AlertDialog.Cancel asChild>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                  >
                    Close
                  </button>
                </AlertDialog.Cancel>
              </div>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      <AlertDialog.Root
        open={appliedRedirectModalOpen}
        onOpenChange={(open) => {
          setAppliedRedirectModalOpen(open);
          if (!open) {
            setAppliedRedirectModalPage(null);
          }
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[205] animate-in fade-in" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-xl shadow-2xl z-[206] border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded bg-amber-100 flex items-center justify-center">
                  <ArrowRight className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <AlertDialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                    Applied Redirection
                    {appliedRedirectModalPage ? `: ${appliedRedirectModalPage.serviceName}` : ''}
                  </AlertDialog.Title>
                  <AlertDialog.Description className="text-sm text-gray-600 leading-relaxed">
                    {appliedRedirectModalLinks.length > 0
                      ? `These active redirect rules are applied to /${appliedRedirectModalPage?.slug || ''}.`
                      : `No active redirection is applied to /${appliedRedirectModalPage?.slug || ''}.`}
                  </AlertDialog.Description>
                </div>
              </div>

              <div className="mt-5 max-h-[360px] overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {appliedRedirectModalLinks.length > 0 ? (
                  appliedRedirectModalLinks.map((redirect) => (
                    <div key={redirect._id} className="p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {redirect.sourceUrl || `/${appliedRedirectModalPage?.slug || ''}`}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          Redirects to {redirect.destinationUrl || 'No destination configured'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {redirect.type && (
                          <Badge variant="soft" color="orange" size="1">
                            {redirect.type}
                          </Badge>
                        )}
                        {redirect.destinationUrl && (
                          <a
                            href={toRedirectHref(redirect.destinationUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-white px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-50 transition-colors"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span>Open</span>
                          </a>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-sm text-gray-500">
                    No active redirection is applied to this page.
                  </div>
                )}
              </div>

              <div className="mt-6 flex items-center justify-end">
                <AlertDialog.Cancel asChild>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                  >
                    Close
                  </button>
                </AlertDialog.Cancel>
              </div>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      <AlertDialog.Root
        open={suspiciousDeleteOpen}
        onOpenChange={(open) => {
          if (!open && !suspiciousDeleteLoading) {
            setSuspiciousDeleteOpen(false);
            setSuspiciousDeletePage(null);
            setSuspiciousDeletePassword('');
            setSuspiciousDeleteError(null);
          }
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[210] animate-in fade-in" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-xl shadow-2xl z-[211] border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <AlertDialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                    Delete suspicious page data
                    {suspiciousDeletePage ? `: ${suspiciousDeletePage.serviceName}` : ''}
                  </AlertDialog.Title>
                  <AlertDialog.Description className="text-sm text-gray-600 leading-relaxed">
                    Warning: /{suspiciousDeletePage?.slug || ''} has no matching page in src/app.
                    This will delete its saved service meta data after admin password verification.
                  </AlertDialog.Description>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label htmlFor="service-meta-suspicious-delete-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Logged-in Admin Email
                  </label>
                  <input
                    id="service-meta-suspicious-delete-email"
                    type="email"
                    value={adminEmail}
                    readOnly
                    className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="service-meta-suspicious-delete-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Password
                  </label>
                  <input
                    id="service-meta-suspicious-delete-password"
                    type="password"
                    value={suspiciousDeletePassword}
                    onChange={(event) => {
                      setSuspiciousDeletePassword(event.target.value);
                      if (suspiciousDeleteError) setSuspiciousDeleteError(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !suspiciousDeleteLoading) {
                        event.preventDefault();
                        performDeleteSuspiciousServiceMeta();
                      }
                    }}
                    autoComplete="current-password"
                    placeholder="Enter your admin password"
                    disabled={suspiciousDeleteLoading}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                  />
                </div>
                {suspiciousDeleteError && (
                  <p className="mt-2 text-sm text-red-600">{suspiciousDeleteError}</p>
                )}
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <AlertDialog.Cancel asChild>
                  <button
                    type="button"
                    disabled={suspiciousDeleteLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                </AlertDialog.Cancel>
                <button
                  type="button"
                  onClick={performDeleteSuspiciousServiceMeta}
                  disabled={suspiciousDeleteLoading}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 hover:bg-red-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {suspiciousDeleteLoading ? 'Verifying...' : 'Verify Delete'}
                </button>
              </div>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Service Meta SEO Modal */}
      <ServiceMetaSeoModal
        isOpen={isSeoModalOpen}
        onClose={closeSeoModal}
        serviceMeta={selectedServiceMetaForSeo}
      />
    </Box>
  );
}
